import React from 'react';
import { Dialog, DialogContent, Box, Tabs, Tab } from '@mui/material';
import { Student } from '@/types';
import { useStudentForm } from './hooks/useStudentForm';
import { useTabNavigation } from './hooks/useTabNavigation';
import { DialogHeader } from './components/DialogHeader';
import { NavigationButtons } from './components/NavigationButtons';
import { TabContent } from './components/TabContent';
import { styles } from './styles';
import { tabs } from './tabs';

interface StudentDetailsProps {
  student: Student;
  onSave: (student: Partial<Student>) => void;
  onCancel: () => void;
  isNew?: boolean;
  allStudents: Student[];
  filteredStudents?: Student[];
  onNavigate?: (studentId: string) => void;
}

export const StudentDetails: React.FC<StudentDetailsProps> = ({
  student,
  onSave,
  onCancel,
  isNew = false,
  allStudents,
  filteredStudents,
  onNavigate
}) => {
  const { formData, hasChanges, handleFieldChange, resetForm } = useStudentForm(student);
  const { activeTab, handleTabChange } = useTabNavigation();

  const studentList = filteredStudents || allStudents;
  const currentIndex = studentList.findIndex(s => s.id === student.id);
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === studentList.length - 1;

  const handleNavigate = (direction: 'first' | 'prev' | 'next' | 'last') => {
    if (!onNavigate) return;

    if (hasChanges) {
      const confirmNavigation = window.confirm(
        'You have unsaved changes. Do you want to save them before navigating?'
      );
      if (confirmNavigation) {
        onSave(formData);
      }
    }

    let newIndex: number;
    switch (direction) {
      case 'first':
        newIndex = 0;
        break;
      case 'prev':
        newIndex = currentIndex - 1;
        break;
      case 'next':
        newIndex = currentIndex + 1;
        break;
      case 'last':
        newIndex = studentList.length - 1;
        break;
      default:
        return;
    }

    if (newIndex >= 0 && newIndex < studentList.length) {
      onNavigate(studentList[newIndex].id);
    }
  };

  return (
    <Dialog
      open
      maxWidth="xl"
      fullWidth
      fullScreen
      sx={styles.dialog}
    >
      <DialogHeader
        student={student}
        onClose={onCancel}
        isNew={isNew}
      />

      <DialogContent sx={styles.content}>
        <Box sx={styles.tabsContainer}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 2 }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon}
                label={tab.label}
                id={`student-tab-${index}`}
                aria-controls={`student-tabpanel-${index}`}
                iconPosition="start"
                sx={styles.tab}
              />
            ))}
          </Tabs>
        </Box>

        <Box sx={styles.tabContent}>
          {tabs.map((tab, index) => (
            <TabContent key={index} value={activeTab} index={index}>
              {React.createElement(tab.component, {
                values: formData,
                onChange: handleFieldChange
              })}
            </TabContent>
          ))}
        </Box>
      </DialogContent>

      {!isNew && onNavigate && (
        <NavigationButtons
          onNavigate={handleNavigate}
          isFirst={isFirst}
          isLast={isLast}
        />
      )}
    </Dialog>
  );
};